import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HeartPulse,
  Briefcase,
  ShoppingBag,
  Cpu,
  Landmark,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { INDUSTRIES } from "@/lib/siteData";

const iconMap = { HeartPulse, Briefcase, ShoppingBag, Cpu, Landmark };

const healthcareDetail = {
  audiences: [
    { title: "Physicians", items: ["Primary care physicians", "Specialists", "Surgeons", "Hospital-based clinicians"] },
    { title: "Allied Healthcare Professionals", items: ["Nurses", "Pharmacists", "Technicians", "Therapists"] },
    { title: "Healthcare Decision-Makers", items: ["Hospital administrators", "Procurement leaders", "Practice managers", "Healthcare executives"] },
    { title: "Patients & Caregivers", items: ["Condition-specific patients", "Treatment-experienced participants", "Caregivers and family members"] },
  ],
  research: [
    "Patient Journey Studies",
    "Brand & Market Research",
    "Product Development Research",
    "Healthcare Professional Studies",
    "Treatment Experience Research",
  ],
  why: [
    "Specialized Recruitment Expertise",
    "Quality-Focused Methodologies",
    "Global Reach",
    "Regulatory Awareness",
  ],
};

export default function Industries() {
  return (
    <div data-testid="industries-page">
      {/* HERO */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg opacity-70 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-24">
          <p className="vd-eyebrow">Industries</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold text-slate-900 max-w-4xl">
            Specialized expertise across sectors where{" "}
            <span className="text-teal-700">research precision matters</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 leading-relaxed">
            From healthcare and financial services to technology and consumer
            brands, we deliver research execution that respects the realities of
            your industry and the rigour your decisions demand.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="border-b border-slate-200">
        <div className="vd-container py-20 md:py-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
          {INDUSTRIES.map((ind, i) => {
            const Icon = iconMap[ind.icon] || HeartPulse;
            return (
              <motion.div
                key={ind.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white p-8 group hover:bg-slate-50 transition-colors"
                data-testid={`industry-card-${ind.icon.toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-display text-xs text-slate-400">
                    0{i + 1}
                  </span>
                  <span className="h-px w-10 bg-slate-200" />
                </div>
                <Icon className="h-7 w-7 text-orange-600 mt-6" />
                <h3 className="mt-5 font-display text-xl font-semibold text-slate-900">
                  {ind.name}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">
                  {ind.blurb}
                </p>
                <ul className="mt-6 space-y-2">
                  {ind.audiences.map((a) => (
                    <li key={a} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-teal-700 mt-0.5 flex-shrink-0" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HEALTHCARE DEEP-DIVE */}
      <section className="border-b border-slate-200 bg-slate-50/60">
        <div className="vd-container py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="vd-eyebrow">Spotlight · Healthcare & Life Sciences</p>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
                Specialized research support for healthcare and life sciences organizations.
              </h2>
              <p className="mt-5 text-slate-600 text-base leading-relaxed">
                Healthcare research demands access to specialized audiences,
                industry knowledge, and rigorous quality standards. VeriData
                Global supports healthcare and life sciences organizations with
                reliable recruitment, data collection, and fieldwork execution
                across global markets.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {healthcareDetail.why.map((w) => (
                  <div
                    key={w}
                    className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-3 bg-white text-sm text-slate-800"
                  >
                    <CheckCircle2 className="h-4 w-4 text-teal-700" />
                    {w}
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <p className="vd-eyebrow">Audiences We Support</p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {healthcareDetail.audiences.map((a) => (
                  <div
                    key={a.title}
                    className="bg-white border border-slate-200 rounded-md p-5"
                  >
                    <p className="font-display text-base font-semibold text-slate-900">
                      {a.title}
                    </p>
                    <ul className="mt-3 space-y-1.5">
                      {a.items.map((it) => (
                        <li key={it} className="text-sm text-slate-600">
                          · {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <p className="vd-eyebrow mt-10">Research Areas</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {healthcareDetail.research.map((r) => (
                  <span
                    key={r}
                    className="text-[13px] text-slate-800 border border-slate-200 rounded-full px-3 py-1.5 bg-white"
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0b192c] text-white relative overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg-dark opacity-50 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-24 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-teal-400">
              Built for your sector
            </p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
              Tell us about your audience. We’ll bring the execution.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="industries-cta"
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-6 py-3.5 rounded-md transition-colors shadow-sm self-start"
          >
            Speak with our team <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
